import React, { useEffect, useState } from 'react';
import '../styles/Auth.css';
import { Input, Button, OrDivider } from '../components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createUser, signInWithGoogle, recordEvent, checkOnAuthStateChanged, completeSignUp, getNames } from '../functions';
import { GoogleLogo } from '../assets/icons';
import logo from '../logo.svg';
import Loading from '../components/Loading';
import { ONBOARDING_PATHS } from './onboardingRoutes';
import { useUser } from '../contexts';
import { isValidEmail, isValidPassword } from '../utils/authValidators';
import { setPendingRedirect, consumePendingRedirect, clearPendingRedirect } from '../utils/pendingRedirect';
import { peekPendingConnectAi, clearPendingConnectAi } from '../utils/pendingConnectAi';

function SignUp() {
	const [signUpWithEmail, setSignUpWithEmail] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [disabled, setDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [lastAuthMethod] = useState(() => localStorage.getItem('lastAuthMethod'));
	const [searchParams] = useSearchParams({});
	const emailParam = searchParams.get("email");
	const navigate = useNavigate();
	const location = useLocation();
	const state = location.state;
	const { hydrateUserData } = useUser();

	useEffect(() => {
		document.title = "Clozers | Sign Up";

		if (emailParam !== null) {
			setEmail(emailParam);
		}

		// Persist any incoming deep-link target durably so it survives a full
		// reload and the SignUp<->Login footer hop. Invite/claim paths are
		// silently refused by the validator (their tokens stay authoritative).
		if (state?.path) setPendingRedirect(state.path);

		const fetchUserData = async() => {
			const user = await checkOnAuthStateChanged();
			if (user.status === 200) {
				// Already signed in but they were sent here by /claim — go
				// straight to claim instead of bouncing to /.
				const pendingClaim = localStorage.getItem('pendingClaimToken');
				if (pendingClaim) {
					localStorage.removeItem('pendingClaimToken');
					navigate(`/claim?token=${pendingClaim}`, { replace: true });
					return;
				}
				navigate("/");
			}
		};

		fetchUserData();
	}, [emailParam, navigate, state]);

	const authenticateEmail = async() => {
		if (!isValidEmail(email)) {
			setErrorMessage("Please enter a valid email address");
			return;
		}
		if (!isValidPassword(password)) {
			setErrorMessage("Use at least 6 characters for your password");
			return;
		}

		setLoading(true);
		setDisabled(true);

		const signUpUser = await createUser(email, password);
		if (signUpUser.status === 200) {
			localStorage.setItem('lastAuthMethod', 'email');
			complete(signUpUser, false);
		} else {
			const code = signUpUser.errorCode;
			if (code === "auth/weak-password") {
				setErrorMessage("Use at least 6 characters for your password");
			} else if (code === "auth/invalid-email") {
				setErrorMessage("Please enter a valid email address");
			} else if (code === "auth/email-already-in-use") {
				setErrorMessage("An account with this email already exists");
			} else if (code === "auth/network-request-failed") {
				setErrorMessage("Connection failed — please try again");
			} else {
				setErrorMessage("Something went wrong — please try again");
			}
			setDisabled(false);
			setLoading(false);
		}
	};

	const googleSignIn = async() => {
		setLoading(true);
		setDisabled(true);
		const signUpUser = await signInWithGoogle();
		if (signUpUser.status === 200) {
			localStorage.setItem('lastAuthMethod', 'google');
			const displayName = signUpUser.user.displayName;
			const displayEmail = signUpUser.user.email;
			const names = getNames(displayName);
			signUpUser.user.firstName = names.firstName;
			signUpUser.user.lastName = names.lastName;
			signUpUser.user.displayName = displayName;
			signUpUser.user.email = displayEmail;

			complete(signUpUser, true);
		} else {
			const code = signUpUser.code;
			if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
				setLoading(false);
				setDisabled(false);
				return;
			} else if (code === "auth/account-exists-with-different-credential") {
				navigate("/login", { state: state });
			} else if (code === "auth/network-request-failed") {
				setErrorMessage("Connection failed — please try again");
			} else {
				setErrorMessage("Google sign-in failed — please try again");
			}
			setLoading(false);
			setDisabled(false);
		}
	};

	const complete = async(signUpUser, googleAuth) => {
		setErrorMessage("");

		const pendingClaim = localStorage.getItem('pendingClaimToken');
		const pendingToken = localStorage.getItem('pendingInviteToken');
		// Claude / MCP connector breadcrumb (parked by ConnectAI when a signed-out
		// user is sent here to sign up). Excluded from the optimistic path so these
		// users take the awaited route that writes the user doc before we return
		// them to the consent screen.
		const pendingConnectAi = peekPendingConnectAi();
		const isDefaultOnboarding = !pendingClaim && !pendingToken && !state?.path && !pendingConnectAi;
		// Email signups are always brand-new (createUser fails on existing
		// emails). For Google we read isNewUser straight off the popup result,
		// so we can tell new vs returning accounts without a server round-trip.
		const isNewUser = googleAuth === false ? true : signUpUser.isNewUser === true;

		// Optimistic path: a brand-new user headed to the standard onboarding
		// flow doesn't need to wait on the user-doc write. The business step's
		// createWorkspace recreates/merges the doc anyway, so we hydrate context
		// from the data we already hold, fire the write in the background, and
		// navigate immediately — removing the createSignupWorkspace round-trip
		// from the perceived signup→redirect latency.
		if (isNewUser && isDefaultOnboarding) {
			// New user → fresh empty workspace → always lands at '/' after
			// onboarding. Discard any preserved deep link (e.g. one seeded by the
			// logged-out RequireOnboarding gate); a company-scoped link wouldn't
			// exist in their new workspace.
			clearPendingRedirect();
			// Email uses the form fields; Google has no form, so its name/email
			// live on the auth result (set in googleSignIn). Either way these are
			// known client-side — no fetch needed to seed onboarding's pre-fill.
			const hydrate = googleAuth
				? { email: signUpUser.user.email, firstName: signUpUser.user.firstName, lastName: signUpUser.user.lastName }
				: { email, firstName, lastName };
			hydrateUserData(hydrate);
			sessionStorage.setItem('freshSignupOnboarding', '1');
			// Background user-doc write + Mixpanel alias + account_created event.
			// Failure is harmless — the business step recreates the doc.
			completeSignUp(signUpUser, googleAuth, email, firstName, lastName).catch((err) => {
				recordEvent("Background Signup Write Failed", { error: err?.message });
			});
			navigate(ONBOARDING_PATHS.business, { replace: true, state: state });
			return;
		}

		// Awaited path: returning Google user, or a claim / invite / state.path
		// redirect. These are rare, not latency-critical, and may read the user
		// doc downstream — so keep the original behaviour exactly.
		const result = await completeSignUp(signUpUser, googleAuth, email, firstName, lastName);
		if (result?.status !== 200) {
			setErrorMessage("Something went wrong — please try again");
			setLoading(false);
			setDisabled(false);
			return;
		}
		if (result.userData) {
			hydrateUserData(result.userData);
		}

		// Pay-first claim takes precedence over EVERY other post-signup
		// route — including the "existing Google user → /" shortcut. A
		// returning user with a parked pendingClaimToken came through /claim
		// for a reason; attaching the subscription is what they're here for.
		if (pendingClaim) {
			localStorage.removeItem('pendingClaimToken');
			navigate(`/claim?token=${pendingClaim}`);
		} else if (pendingConnectAi) {
			// Claude / MCP connector signup: the standard signup just captured the
			// user's name + wrote their user doc (full parity with web). Send them
			// back to the consent screen to Authorize — never into web onboarding,
			// which they instead complete inside the AI client.
			clearPendingConnectAi();
			recordEvent("connect_ai_signed_up", {
				method: googleAuth ? 'google' : 'email',
				mode: (googleAuth && result.profileCreated === false) ? 'signin' : 'create'
			});
			navigate(`/connect-ai?${pendingConnectAi}`);
		} else if (googleAuth && result.profileCreated === false) {
			// Returning Google user (has a workspace) → honour their preserved
			// deep link. The breadcrumb was seeded from state.path on mount.
			const redirect = consumePendingRedirect();
			navigate(redirect || "/");
		} else if (pendingToken) {
			localStorage.removeItem('pendingInviteToken');
			navigate(`/accept-invite?token=${pendingToken}`);
		} else {
			// Brand-new user falling through to onboarding → discard any preserved
			// deep link; they always finish onboarding at '/'.
			clearPendingRedirect();
			sessionStorage.setItem('freshSignupOnboarding', '1');
			navigate(ONBOARDING_PATHS.business, { replace: true, state: state });
		}

		setLoading(false);
		setDisabled(false);
	};

	const activateEmailSignup = () => {
		setSignUpWithEmail(true);
		setErrorMessage("");
		recordEvent("Activate Email Signup", {});
	};

	const emailButtonDisabled = firstName === "" || lastName === "" || email === "" || password === "" || disabled;

    return (
        <div className="auth-outer-container">
            {loading && (
                <div className="auth-loading-overlay">
                    <Loading />
                </div>
            )}
            <div className="auth-inner-container">

                <div className="auth-logo-container">
                    <img src={logo} alt="Clozers" className="auth-logo" />
                </div>

                <div className="auth-header-container">
                    <h1 className="heading-large-bold colour-primary">
                        Find the best deals in your inbox
                    </h1>
                </div>

                <div className="auth-google-section">
                    <Button
                        buttonType="third-party-button"
                        size="large"
                        text="Continue with Google"
                        cta={googleSignIn}
                        disabled={disabled}
                        leftIcon={<GoogleLogo />}
                        leftIconClass=""
                        rightIcon={null}
                        rightIconClass=""
                        alt=""
                        standardIcon={false}
                        badge={false}
                        badgeVal={null}
                        badgeBG={null}
                        buttonRef={null}
                        spinner={loading}
                        goldAccent={lastAuthMethod === "google"}
                    />
                    {lastAuthMethod === "google" && (
                        <span className="label-regular colour-secondary auth-method-hint">
                            You used this last time
                        </span>
                    )}
                </div>

                <OrDivider />

                <div className="auth-email-section">
                    {signUpWithEmail === false ? (
                        <span
                            className="body-regular-underline colour-link cursor-pointer"
                            onClick={activateEmailSignup}
                        >
                            Sign up with email
                        </span>
                    ) : (
                        <div className="auth-form">
                            <div className="auth-name-row">
                                <div>
                                    <Input
                                        label={"First name"}
                                        labelType={"inside"}
                                        secondaryLabelGrey={null}
                                        info={false}
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        placeholder={"Ben"}
                                        type="text"
                                        leftUnit={null}
                                        rightUnit={null}
                                        unitSwitch={false}
                                        leftUnitSelected={true}
                                        unitSwitchFunc={null}
                                        disabled={loading}
                                        multiline={false}
                                        error={false}
                                        onMouseEnter={null}
                                        onMouseLeave={null}
                                        reset={false}
                                        resetFunc={null}
                                        resetPosition={null}
                                        focusMultiline={false}
                                        prompt={false}
                                    />
                                </div>
                                <div>
                                    <Input
                                        label={"Last name"}
                                        labelType={"inside"}
                                        secondaryLabelGrey={null}
                                        info={false}
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        placeholder={"Avery"}
                                        type="text"
                                        leftUnit={null}
                                        rightUnit={null}
                                        unitSwitch={false}
                                        leftUnitSelected={true}
                                        unitSwitchFunc={null}
                                        disabled={loading}
                                        multiline={false}
                                        error={false}
                                        onMouseEnter={null}
                                        onMouseLeave={null}
                                        reset={false}
                                        resetFunc={null}
                                        resetPosition={null}
                                        focusMultiline={false}
                                        prompt={false}
                                    />
                                </div>
                            </div>
                            <Input
                                label={"Email"}
                                labelType={"inside"}
                                secondaryLabelGrey={null}
                                info={false}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={"youremail@email.com"}
                                type="email"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={loading}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                            <Input
                                label={"Password"}
                                labelType={"inside"}
                                secondaryLabelGrey={null}
                                info={false}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={"••••••••••"}
                                type="password"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={loading}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text={loading ? "Creating account…" : "Create account"}
                                cta={authenticateEmail}
                                disabled={emailButtonDisabled}
                                leftIcon={null}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={loading}
                                goldAccent={lastAuthMethod === "email"}
                            />
                            {lastAuthMethod === "email" && (
                                <span className="label-regular colour-secondary auth-method-hint">
                                    You used this last time
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {errorMessage !== "" && (
                    <div className="auth-error">
                        <span className="body-regular colour-error text-align-center">
                            {errorMessage}
                        </span>
                    </div>
                )}

                <div className="auth-footer">
                    <span className="body-regular colour-primary text-align-center">
                        Already have an account?{" "}
                        <span
                            onClick={() => !disabled && navigate("/login", { state: state })}
                            className={`body-regular-underline colour-link ${disabled ? "cursor-disabled" : "cursor-pointer"}`}
                        >
                            Login
                        </span>
                    </span>
                    <span className="label-regular colour-secondary text-align-center">
                        By signing up you agree to our{" "}
                        <span
                            onClick={() => !disabled && navigate("/terms-conditions", { state: state })}
                            className={`colour-link underline ${disabled ? "cursor-disabled" : "cursor-pointer"}`}
                        >
                            terms of service
                        </span>
                    </span>
                </div>

            </div>
        </div>
    );
};

export default SignUp;
